import type { JSX } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Link from "next/link";
import { CookieSettingsLink } from "@/components/consent/CookieSettingsLink";

const footerColumns = [
  {
    sections: [
      {
        title: "Start Your Business",
        links: [
          { label: "Register a company – For Locals", href: "/hong-kong-company-formation" },
          { label: "Register a company – For Foreigners", href: "/hong-kong-company-formation" },
          { label: "Registered Office Address", href: "/corporate-secretary" },
        ],
      },
      {
        title: "Manage your finances",
        links: [
          { label: "Accounting Services", href: "/accounting-services-hong-kong" },
          { label: "Audit & Tax Filing", href: "/tax-optimization" },
          { label: "Payroll Services", href: "/bookkeeping-services-hong-kong" },
        ],
      },
    ],
  },
  {
    sections: [
      {
        title: "Stay compliant",
        links: [
          { label: "Company secretary", href: "/corporate-secretary" },
          { label: "Electronic signature", href: "/corporate-secretary" },
          { label: "Annual Return Filing", href: "/corporate-secretary" },
        ],
      },
      {
        title: "Resources",
        links: [{ label: "Electronic signatures", href: "/corporate-secretary" }],
      },
      {
        title: "Customer stories",
        links: [{ label: "See their experiences", href: "/about" }],
      },
    ],
  },
  {
    sections: [
      {
        title: "Company",
        links: [
          { label: "About Us", href: "/about" },
          { label: "Corporate Social Responsibility", href: "/about" },
          { label: "Resources", href: "/blogs" },
          { label: "Careers at MuteTaxes", href: "/contact" },
          { label: "Affiliates", href: "/contact" },
          { label: "Contact us", href: "/contact" },
        ],
      },
    ],
  },
];

export const SiteFooterSection = (): JSX.Element => {
  return (
    // Deliberately NOT elevated above StickyBottomCta's `z-40` (unlike
    // ClientTrustAndTestimonialsSection, the section above this one, which is
    // `z-50`). That section's opaque z-50 background is what hides the fixed
    // CTA bar as it scrolls up; leaving the footer at the default stacking
    // level means the bar re-emerges over it the moment that section's
    // bottom edge clears the bar's position — the same scroll-driven edge
    // that hid it, now revealing it, so hide and reveal mirror each other
    // with no separate logic. The footer has no CTA buttons of its own, so
    // the bar staying visible here is intentional. The bottom padding keeps
    // it clear of the copyright/ISO row once it's back.
    <footer className="flex w-full flex-col bg-transparent pb-24 text-white sm:pb-20">
      {/* `relative z-[60]` alone only wins the paint-order comparison against
          StickyBottomCta's z-40 — it doesn't make anything opaque. Without a
          real background, the bar still showed through wherever this section
          has no content of its own (the gaps between nav columns, the empty
          space beside the card), so `bg-black` gives it full coverage across
          the whole box, the same way ClientTrustAndTestimonialsSection hides
          the bar earlier on. The section below (social icons/copyright) is
          intentionally left alone so the bar still shows over it, per the
          note on <footer> above. */}
      <section className="relative z-[60] w-full bg-black px-6 shadow-[1px_1px_7.5px_#00000033] sm:px-10 lg:px-16 xl:px-[150px]">
        <div className="grid min-h-[477px] grid-cols-1 gap-10 py-[50px] pt-[60px] md:grid-cols-2 xl:grid-cols-4 xl:gap-0">
          {footerColumns.map((column, columnIndex) => (
            <nav
              key={`footer-column-${columnIndex}`}
              aria-label={`Footer navigation ${columnIndex + 1}`}
              className="flex flex-col gap-5 pt-[18px]"
            >
              {column.sections.map((section, sectionIndex) => (
                <section
                  key={section.title}
                  className={sectionIndex === 0 ? "" : "pt-5"}
                  aria-labelledby={`footer-section-${columnIndex}-${sectionIndex}`}
                >
                  <h2
                    id={`footer-section-${columnIndex}-${sectionIndex}`}
                    className="[font-family:'Helvetica_Neue-Regular',Helvetica] text-xl font-normal leading-7 tracking-[0] text-white"
                  >
                    {section.title}
                  </h2>
                  <ul className="mt-5 flex flex-col gap-[9.5px]">
                    {section.links.map((link) => (
                      <li key={link.label}>
                        <Button
                          asChild
                          variant="link"
                          className="h-auto p-0 font-inter text-left text-sm font-normal leading-[21px] tracking-[0] text-white no-underline hover:text-white hover:no-underline"
                        >
                          <Link href={link.href}>{link.label}</Link>
                        </Button>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </nav>
          ))}

          <Card className="h-fit rounded-[20px] border border-solid border-[#ffffff29] bg-[#0f1218] text-white shadow-none">
            <CardContent className="flex flex-col gap-[3px] p-5">
              <p className="font-inter text-center text-xl font-medium leading-6 tracking-[0.1px] text-white">
                Looking to connect with
                <br />
                fellow entrepreneurs?
              </p>
              <Button
                asChild
                variant="outline"
                className="mt-3 h-auto w-full rounded-[10px] border-2 border-[#0b3bff] bg-transparent px-5 py-[9px] font-inter text-lg font-medium leading-[22px] tracking-[0.3px] text-[#0b3bff] hover:border-[#0b3bff] hover:bg-transparent hover:text-[#0b3bff]"
              >
                <a href="https://www.instagram.com/mutetaxesofficial/" target="_blank" rel="noopener noreferrer">
                  Join the community
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
      <section className="w-full border-t border-solid border-[#585d7080] px-6 sm:px-10 lg:px-16 xl:px-[150px]">
        <div className="flex flex-col items-center justify-between gap-4 py-2.5 xl:flex-row xl:gap-0">
          <div className="flex shrink-0 items-center gap-3">
            <a href="https://www.facebook.com/people/MuteTaxes/61580890892503/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <img className="h-10 w-10" alt="Facebook" src="/home-v3/fb.svg" />
            </a>
            <a href="https://www.instagram.com/mutetaxesofficial/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <img className="h-10 w-10" alt="Instagram" src="/home-v3/insta.svg" />
            </a>
          </div>
          <div className="py-[9.4px] pb-2.5 xl:text-right">
            <p className="font-dm-sans text-center text-xs font-medium leading-[16.8px] tracking-[0] text-white xl:text-right">
              © 2026 MuteTaxes Hong Kong Limited
            </p>
            <p className="mt-2 flex items-center justify-center gap-2 text-xs font-medium tracking-[0] text-white/60 xl:justify-end">
              <Link href="/privacy" className="hover:text-white hover:underline">
                Privacy Policy
              </Link>
              {/* Terms & Conditions link intentionally omitted until final,
                  legally-reviewed copy replaces the draft at
                  app/(marketing)/terms/page.tsx (currently 404s on purpose).
                  Restore alongside removing that page's notFound() call. */}
              <span aria-hidden="true">·</span>
              <CookieSettingsLink className="hover:text-white hover:underline" />
            </p>
          </div>
        </div>
      </section>
    </footer>
  );
};
